import {
    Box,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    styled,
    Paper,
    ImageList,
    ImageListItem,
    Modal,
    useTheme,
    CircularProgress,
    Tooltip
} from "@mui/material";
import { Edit, Delete, LocationOn, Close } from "@mui/icons-material";
import { useState } from "react";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import apiService from "Network/apiService";
import AddEventModal from "./AddEventModal";
import { formatDate } from "./formatDate";
import EventAttendeeModal from "./EventAttendeeModal";
import ConfirmationDialog from "components/ConfirmationDialog";
import toast from "react-hot-toast";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    padding: "16px 20px",
    color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black,
    borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
    whiteSpace: "nowrap",
    "&.header": {
        fontWeight: 600,
        fontSize: "0.875rem",
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:hover": {
        backgroundColor: theme.palette.mode === 'dark' 
            ? "rgba(255, 255, 255, 0.05)" 
            : "rgba(0, 0, 0, 0.05)",
    },
    "& > .MuiTableCell-root": {
        borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
    },
}));

const ImagePreviewContainer = styled(Box)(({ theme }) => ({
    maxWidth: '200px',
    margin: '8px 0',
}));

const ThumbnailImage = styled('img')({
    width: '60px',
    height: '60px',
    objectFit: 'cover',
    borderRadius: '4px',
    cursor: 'pointer',
    margin: '2px',
    '&:hover': {
        opacity: 0.8,
    },
});

const ModalImage = styled('img')({
    maxWidth: '90vw',
    maxHeight: '90vh',
    objectFit: 'contain',
});

const ImageModal = styled(Modal)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
});

const CloseButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    right: theme.spacing(2),
    top: theme.spacing(2),
    color: 'white',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
    },
}));

const EventTable = ({ events, searchQuery, refreshTrigger, onDeleteSuccess }) => {
    const theme = useTheme();
    const [openModal, setOpenModal] = useState(false);
    const [editData, setEditData] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [attendeeModalOpen, setAttendeeModalOpen] = useState(false);
    const [selectedEventAttendees, setSelectedEventAttendees] = useState(null);
    const [selectedEventData, setSelectedEventData] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [eventToDelete, setEventToDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Filter events based on search query
    const filteredEvents = events.filter(event => {
        if (!searchQuery) return true;
        
        const searchLower = searchQuery.toLowerCase();
        return (
            event.title?.toLowerCase().includes(searchLower) ||
            event.description?.toLowerCase().includes(searchLower) ||
            event.city?.toLowerCase().includes(searchLower) ||
            event.address?.toLowerCase().includes(searchLower)
        );
    });

    const handleDelete = async (id) => {
        try {
            setDeleteLoading(true);
            const response = await apiService.delete(
                `${API_ENDPOINTS.DELETE_event}?event_id=${id}`
            );

            if (response?.success) {
                toast.success("Event deleted successfully");
                onDeleteSuccess(); // This will trigger the refresh
            } else {
                toast.error(response?.message || "Failed to delete event");
            }
        } catch (error) {
            console.error("Delete error:", error);
            toast.error("Failed to delete event");
        } finally {
            setDeleteLoading(false);
            setDeleteDialogOpen(false);
            setEventToDelete(null);
        }
    };

    const handleDeleteClick = (event) => {
        setEventToDelete(event);
        setDeleteDialogOpen(true);
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setEventToDelete(null);
    };

    const handleImageClick = (imageUrl) => {
        console.log("🚀 ~ handleImageClick ~ imageUrl:", imageUrl)
        setSelectedImage(imageUrl);
    };

    const handleAttendeeClick = async (eventId) => {
        try {
            const response = await apiService.get(`${API_ENDPOINTS.GET_event_attendee}?event_id=${eventId}`);
            if (response.success) {
                setSelectedEventData(response.data.event);
                setSelectedEventAttendees(response.data.attendees);
                setAttendeeModalOpen(true);
            }
        } catch (error) {
            console.error('Error fetching attendees:', error);
        }
    };

    const truncateText = (text, maxLength = 10) => {
        if (!text) return '';
        return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
    };

    return (
        <Box sx={{ width: "100%", p: 2 }}>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <StyledTableCell className="header">Event Title</StyledTableCell>
                            <StyledTableCell className="header">Images</StyledTableCell>
                            <StyledTableCell className="header">Description</StyledTableCell>
                            <StyledTableCell className="header">Date</StyledTableCell>
                            <StyledTableCell className="header">City</StyledTableCell>
                            <StyledTableCell className="header">Address</StyledTableCell>
                            <StyledTableCell className="header" align="center">Location</StyledTableCell>
                            <StyledTableCell className="header" align="center">Attendee</StyledTableCell>
                            <StyledTableCell className="header" align="center">Actions</StyledTableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {!events ? (
                            <StyledTableRow>
                                <StyledTableCell 
                                    colSpan={9} 
                                    align="center" 
                                    sx={{ py: 8 }}
                                >
                                    <CircularProgress />
                                </StyledTableCell>
                            </StyledTableRow>
                        ) : filteredEvents && filteredEvents.length > 0 ? (
                            filteredEvents.map((event) => (
                                <StyledTableRow key={event.id}>
                                    <StyledTableCell sx={{ 
                                        color: theme.palette.mode === 'dark' ? '#fff' : '#000',
                                        fontWeight: 500,
                                        maxWidth: '150px'
                                    }}>
                                        <Tooltip 
                                            title={event.title} 
                                            enterDelay={500}
                                            placement="bottom-start"
                                        >
                                            <span>{truncateText(event.title, 20)}</span>
                                        </Tooltip>
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {event.images && event.images.length > 0 ? (
                                            <ImagePreviewContainer>
                                                <ImageList cols={2} rowHeight={60} gap={2}>
                                                    {event.images.slice(0, 4).map((image, index) => (
                                                        <ImageListItem key={index}>
                                                            <ThumbnailImage
                                                                src={`${API_ENDPOINTS.BASE_URL}${image.image}`}
                                                                alt={`Event ${index + 1}`}
                                                                onClick={() => handleImageClick(`${API_ENDPOINTS.BASE_URL}${image.image}`)}
                                                            />
                                                        </ImageListItem>
                                                    ))}
                                                </ImageList>
                                                {event.images.length > 4 && (
                                                    <Box sx={{ 
                                                        mt: 1,
                                                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)'
                                                    }}>
                                                        +{event.images.length - 4} more
                                                    </Box>
                                                )}
                                            </ImagePreviewContainer>
                                        ) : (
                                            <Box sx={{ 
                                                color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                                                fontStyle: 'italic'
                                            }}>
                                                No images
                                            </Box>
                                        )}
                                    </StyledTableCell>
                                    <StyledTableCell sx={{
                                        maxWidth: '250px',
                                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                                    }}>
                                        <Tooltip 
                                            title={event.description} 
                                            enterDelay={500}
                                            placement="bottom-start"
                                        >
                                            <span>{truncateText(event.description, 15)}</span>
                                        </Tooltip>
                                    </StyledTableCell>
                                    <StyledTableCell sx={{ 
                                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)'
                                    }}>
                                        {formatDate(event.startDate, event.endDate)}
                                    </StyledTableCell>
                                    <StyledTableCell sx={{ 
                                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                                        maxWidth: '160px'
                                    }}>
                                        <Tooltip 
                                            title={event.city} 
                                            enterDelay={500}
                                            placement="bottom-start"
                                        >
                                            <span>{truncateText(event.city, 12)}</span>
                                        </Tooltip>
                                    </StyledTableCell>
                                    <StyledTableCell sx={{
                                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                                        maxWidth: '180px'
                                    }}>
                                        <Tooltip 
                                            title={event.address} 
                                            enterDelay={500}
                                            placement="bottom-start"
                                        >
                                            <span>{truncateText(event.address, 25)}</span>
                                        </Tooltip>
                                    </StyledTableCell>
                                    <StyledTableCell align="center">
                                        <IconButton
                                            size="small"
                                            sx={{
                                                color: 'primary.main',
                                                '&:hover': {
                                                    backgroundColor: theme.palette.mode === 'dark' 
                                                        ? 'rgba(0, 120, 255, 0.1)'
                                                        : 'rgba(0, 120, 255, 0.2)'
                                                }
                                            }}
                                            onClick={() => window.open(event.map_location, '_blank')}
                                        >
                                            <LocationOn />
                                        </IconButton>
                                    </StyledTableCell>
                                    <StyledTableCell 
                                        align="center"
                                        onClick={() => handleAttendeeClick(event.id)}
                                        sx={{ 
                                            cursor: 'pointer',
                                            '&:hover': {
                                                backgroundColor: theme.palette.mode === 'dark' 
                                                    ? 'rgba(255, 255, 255, 0.1)' 
                                                    : 'rgba(0, 0, 0, 0.05)',
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                color: 'primary.main',
                                                fontWeight: 500,
                                                padding: '4px 12px',
                                                borderRadius: '16px',
                                                border: '1px solid',
                                                borderColor: 'primary.main',
                                                transition: 'all 0.2s',
                                                '&:hover': {
                                                    backgroundColor: 'primary.main',
                                                    color: 'white',
                                                }
                                            }}
                                        >
                                            {event.attendee_count} Attendees
                                        </Box>
                                    </StyledTableCell>
                                    <StyledTableCell align="center">
                                        <IconButton
                                            size="small"
                                            sx={{
                                                color: theme.palette.mode === 'dark' ? 'white' : 'rgba(0, 0, 0, 0.7)',
                                                '&:hover': {
                                                    backgroundColor: theme.palette.mode === 'dark' 
                                                        ? 'rgba(255, 255, 255, 0.1)'
                                                        : 'rgba(0, 0, 0, 0.1)'
                                                }
                                            }}
                                            onClick={() => handleDeleteClick(event)}
                                        >
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </StyledTableCell>
                                </StyledTableRow>
                            ))
                        ) : (
                            <StyledTableRow>
                                <StyledTableCell 
                                    colSpan={9} 
                                    align="center" 
                                    sx={{ 
                                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)'
                                    }}
                                >
                                    No events available
                                </StyledTableCell>
                            </StyledTableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <AddEventModal
                open={openModal}
                onClose={() => {
                    setOpenModal(false);
                    setEditData(null);
                }}
                edit={Boolean(editData)}
                data={editData}
            />

            {/* Image Preview Modal */}
            <ImageModal
                open={Boolean(selectedImage)}
                onClose={() => setSelectedImage(null)}
                BackdropProps={{
                    style: { backgroundColor: 'rgba(0, 0, 0, 0.9)' }
                }}
            >
                <Box sx={{ position: 'relative' }}>
                    <CloseButton onClick={() => setSelectedImage(null)}>
                        <Close />
                    </CloseButton>
                    {selectedImage && (
                        <ModalImage
                            src={selectedImage}
                            alt="Event"
                        />
                    )}
                </Box>
            </ImageModal>

            <EventAttendeeModal
                open={attendeeModalOpen}
                onClose={() => {
                    setAttendeeModalOpen(false);
                    setSelectedEventAttendees(null);
                    setSelectedEventData(null);
                }}
                eventData={selectedEventData}
                attendees={selectedEventAttendees}
            />

            {/* Add Confirmation Dialog */}
            <ConfirmationDialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                onConfirm={() => handleDelete(eventToDelete?.id)}
                title="Delete Event"
                message={`Are you sure you want to delete event "${eventToDelete?.title}"?`}
                confirmButtonText="Delete"
                confirmButtonColor="error"
                isLoading={deleteLoading}
            />
        </Box>
    );
};

export default EventTable;